import React, { useEffect, useRef, useState } from 'react';

import { setSnackbar } from 'flow/actions/app-actions';
import {
    closeMeetingJoinInfo,
    fetchAllMeetingNumbers,
    requestPin,
} from 'flow/actions/meeting-actions';
import Button from 'flow/components/ui-components/Button';
import DropdownMenuComponent from 'flow/components/ui-components/DropdownMenuComponent';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import {
    selectActiveMeetingCode,
    selectActiveMeetingNumber,
    selectActiveMeetingPin,
    selectAllMeetingNumbersSorted,
} from 'flow/selectors/meeting-selectors';
import { getOwnCountryCodeOrNull } from 'flow/selectors/selectors';
import { IS_UAE_HOST } from 'flow/utils/browser-utils-TW';
import { prettyFormatE164Number } from 'flow/utils/call-utils';
import { getConferenceLink } from 'flow/utils/meeting-utils';
import MeetingJoinOnAnotherDeviceModal from './MeetingJoinOnAnotherDeviceModal';

/**
 * Pixel-matched, dark-themed joining-info panel content (Meetings UX bug board
 * Figma "Suggestion"), framed by the shared MeetingRightPanel slot. Serves
 * every meeting type — MeetingRightPanel auto-opens this one for instant
 * meetings, everyone else opens it via the footer button/more-menu item.
 */
const MeetingJoinInfoPanel = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const meetingCode = useAppSelector(selectActiveMeetingCode);
    const meetingPin = useAppSelector(selectActiveMeetingPin);
    const meetingNumber = useAppSelector(selectActiveMeetingNumber);
    const ownCountryCode = useAppSelector(getOwnCountryCodeOrNull);
    const meetingNumbers = useAppSelector(selectAllMeetingNumbersSorted);

    const countryOptions = Array.from(meetingNumbers.values()).map(
        (number) => ({
            key: number.countryCode,
            label: number.countryNameInEnglish,
        }),
    );

    const [countryCode, setCountryCode] = useState(
        IS_UAE_HOST ? 'ae' : (ownCountryCode?.toLowerCase() ?? null),
    );
    const [countryNumber, setCountryNumber] = useState(meetingNumber);

    useEffect(() => {
        dispatch(fetchAllMeetingNumbers());
    }, [dispatch]);

    useEffect(() => {
        const country = meetingNumbers?.get(countryCode);
        if (country) {
            setCountryNumber(
                prettyFormatE164Number(
                    country.conferenceNumber,
                    'wantInternational',
                ),
            );
        }
    }, [meetingNumbers, countryCode]);

    const conferenceLink = getConferenceLink(meetingCode, countryCode);

    const [showJoinOnAnotherDevice, setShowJoinOnAnotherDevice] =
        useState(false);
    const [loadingPin, setLoadingPin] = useState(false);
    const isMountedRef = useRef(true);
    useEffect(
        () => () => {
            isMountedRef.current = false;
        },
        [],
    );

    const revealPin = async () => {
        setLoadingPin(true);
        try {
            const resultPin = await dispatch(requestPin(meetingCode));
            if (!resultPin) {
                dispatch(
                    setSnackbar({
                        message: i18next.t(
                            'videoconference.join_info.show_pin_failed',
                        ),
                    }),
                );
            }
        } finally {
            if (isMountedRef.current) {
                setLoadingPin(false);
            }
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            dispatch(
                setSnackbar({
                    message: i18next.t('snackbar.copied_to_clipboard'),
                }),
            );
        });
    };

    const copyJoiningDetails = () =>
        copyToClipboard(
            [
                conferenceLink,
                `${i18next.t('videoconference.controlpanel.dial_in')} ${countryNumber}`,
                `${i18next.t('videoconference.controlpanel.pin_short')} ${meetingPin}`,
            ]
                .filter(Boolean)
                .join('\n'),
        );

    return (
        <>
            {showJoinOnAnotherDevice && (
                <MeetingJoinOnAnotherDeviceModal
                    dialInNumber={countryNumber}
                    pin={meetingPin}
                    conferenceLink={conferenceLink}
                    onClose={() => setShowJoinOnAnotherDevice(false)}
                />
            )}

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl bg-gray-800 text-white">
                <div className="flex items-center justify-between px-4 py-3 text-base font-bold">
                    <span>
                        {i18next.t('videoconference.controlpanel.meeting_info')}
                    </span>
                    <button
                        className="flex cursor-pointer items-center border-none bg-transparent p-0"
                        onClick={() => dispatch(closeMeetingJoinInfo())}
                        aria-label={i18next.t(
                            'videoconference.controlpanel.close',
                        )}
                        type="button"
                    >
                        <Icon iconKey="close" />
                    </button>
                </div>

                {/* -mt-1 pt-1: room for the focus ring of the country
                    dropdown at the top, taken from the header's padding. */}
                <div className="-mt-1 flex flex-1 flex-col gap-3 overflow-auto px-4 pt-1 pb-4 text-sm">
                    <DropdownMenuComponent
                        options={countryOptions}
                        title={
                            countryOptions.find(
                                (option) => option.key === countryCode,
                            )?.label
                        }
                        onItemSelected={setCountryCode}
                        disabled={IS_UAE_HOST}
                        menuButtonClassName="!bg-transparent !text-white !p-0 !text-sm !font-normal"
                        menuItemsClassName="!bg-gray-700 outline-none"
                        menuItemClassName="!text-white hover:!bg-gray-600"
                    />

                    <div className="flex items-center gap-2">
                        <div className="truncate text-gray-300">
                            {conferenceLink}
                        </div>
                        <IconButton
                            onClick={() => copyToClipboard(conferenceLink)}
                            className="ms-auto size-6 shrink-0"
                            tooltipLabel={i18next.t(
                                'videoconference.join_info.copy_link',
                            )}
                        >
                            <Icon
                                iconKey="copyOutlined"
                                size="1rem"
                                className="text-gray-300"
                            />
                        </IconButton>
                    </div>

                    <div>
                        {i18next.t('videoconference.controlpanel.dial_in')}{' '}
                        {countryNumber}
                    </div>

                    <div className="flex items-center gap-2">
                        {i18next.t('videoconference.controlpanel.pin_short')}
                        {meetingPin && <span>{meetingPin}</span>}
                        {!meetingPin && (
                            <Button
                                size="small"
                                color="basic"
                                className="ms-auto !rounded-lg !bg-gray-700 px-3 py-1.5 !text-white hover:!bg-gray-600"
                                onClick={revealPin}
                                disabled={loadingPin}
                                isLoading={loadingPin}
                            >
                                {i18next.t(
                                    'videoconference.join_info.show_pin',
                                )}
                            </Button>
                        )}
                    </div>

                    <button
                        className="-mx-2 flex cursor-pointer items-center gap-2 rounded-lg border-none bg-transparent px-2 py-2 text-start text-gray-300 hover:bg-gray-700 hover:text-white"
                        onClick={copyJoiningDetails}
                        type="button"
                    >
                        <Icon iconKey="copyOutlined" size="1rem" />
                        {i18next.t(
                            'videoconference.controlpanel.copy_joining_details',
                        )}
                    </button>

                    <hr className="my-1 border-t border-gray-700" />

                    {(meetingPin || conferenceLink) && (
                        <Button
                            className="!h-11 !min-w-0 self-start !rounded-xl !bg-gray-700 px-6 !text-white hover:!bg-gray-600"
                            onClick={() => setShowJoinOnAnotherDevice(true)}
                        >
                            {i18next.t(
                                'videoconference.lobby.join_other_device',
                            )}
                        </Button>
                    )}
                </div>
            </div>
        </>
    );
};

export default MeetingJoinInfoPanel;
